import { apiClient } from '../lib/apiClient';
import { getAllLocalDrafts, deleteLocalDraft } from '../lib/localDraftStore';

export interface DraftInspectionController {
  id: string;
  controller_number: number;
  description: string;
  location: string;
  photo_url: string | null;
  zones: Array<{
    id: string;
    zone_number: number;
    type: string;
    work_codes: string;
    notes: string;
    photo_urls: string[];
    issues?: Array<{
      tempId: string;
      work_code: string;
      notes: string;
      photo_urls: string[];
    }>;
  }>;
}

export interface DraftInspection {
  reportId: string;
  jobSiteId: string;
  jobSiteName: string;
  month: string;
  technicians: string[];
  totalDraftsForMonth: number;
  lastUpdated: string;
  controllers: DraftInspectionController[];
}

interface ServerReport {
  id: string;
  job_site_id: string | null;
  job_site_name: string;
  month: string;
  technicians: string[];
  user_id: string | null;
  is_draft: boolean | null;
  created_at: string;
  updated_at: string;
}

interface ServerZoneInspection {
  id: string;
  report_id: string;
  zone_id: string | null;
  controller_id: string | null;
  zone_number: number;
  type: string | null;
  work_codes: string;
  notes: string;
  photo_urls: string[];
}

interface ServerZoneIssue {
  id: string;
  zone_inspection_id: string;
  work_code: string;
  notes: string;
  photo_urls: string[];
}

interface ServerController {
  id: string;
  controller_number: number;
  description: string;
  location: string;
  photo_url: string | null;
}

/**
 * Per phase-1 recipe: server's GET /api/reports already applies §3.3 visibility,
 * so for the current user's drafts we filter the response client-side. For the
 * draft tree we reuse GET /api/reports/:id/tree (Slice A's endpoint).
 */
export async function checkForDraftReport(
  userId: string,
  month: string,
  jobSiteId?: string,
): Promise<DraftInspection | null> {
  const allReports = await apiClient.get<ServerReport[]>('/api/reports');
  const myDrafts = allReports.filter(
    (r) =>
      r.user_id === userId &&
      r.month === month &&
      r.is_draft === true &&
      // Scope to the property being started, so "resume the in-progress
      // inspection" only triggers for the SAME property (not any draft that
      // month) — prevents duplicate in-progress reports per property.
      (!jobSiteId || r.job_site_id === jobSiteId),
  );
  if (myDrafts.length === 0) return null;

  myDrafts.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const mostRecent = myDrafts[0];

  const [zoneInspections, allIssues, controllers] = await Promise.all([
    apiClient.get<ServerZoneInspection[]>(`/api/zone-inspections?reportId=${mostRecent.id}`),
    apiClient.get<ServerZoneIssue[]>(`/api/zone-issues?reportId=${mostRecent.id}`),
    mostRecent.job_site_id
      ? apiClient.get<ServerController[]>('/api/controllers').then((all) =>
          all.filter((c) => (c as unknown as { job_site_id: string }).job_site_id === mostRecent.job_site_id),
        )
      : Promise.resolve([] as ServerController[]),
  ]);

  const issuesByZi = new Map<string, ServerZoneIssue[]>();
  for (const issue of allIssues) {
    const list = issuesByZi.get(issue.zone_inspection_id) ?? [];
    list.push(issue);
    issuesByZi.set(issue.zone_inspection_id, list);
  }

  const controllerMap = new Map<string, DraftInspectionController>();
  for (const c of controllers) {
    controllerMap.set(c.id, {
      id: c.id,
      controller_number: c.controller_number,
      description: c.description ?? '',
      location: c.location ?? '',
      photo_url: c.photo_url ?? null,
      zones: [],
    });
  }

  for (const zi of zoneInspections) {
    if (!zi.controller_id || !controllerMap.has(zi.controller_id)) continue;
    const issues = (issuesByZi.get(zi.id) ?? []).map((issue) => ({
      tempId: issue.id,
      work_code: issue.work_code,
      notes: issue.notes ?? '',
      photo_urls: issue.photo_urls ?? [],
    }));
    controllerMap.get(zi.controller_id)!.zones.push({
      id: zi.zone_id ?? zi.id,
      zone_number: zi.zone_number,
      type: zi.type ?? '',
      work_codes: zi.work_codes ?? '',
      notes: zi.notes ?? '',
      photo_urls: zi.photo_urls ?? [],
      issues: issues.length > 0 ? issues : undefined,
    });
  }

  const sortedControllers = Array.from(controllerMap.values()).sort(
    (a, b) => a.controller_number - b.controller_number,
  );

  return {
    reportId: mostRecent.id,
    jobSiteId: mostRecent.job_site_id ?? '',
    jobSiteName: mostRecent.job_site_name,
    month: mostRecent.month,
    technicians: mostRecent.technicians,
    totalDraftsForMonth: myDrafts.length,
    lastUpdated: mostRecent.updated_at,
    controllers: sortedControllers,
  };
}

export async function deleteAllDraftReports(userId: string, month: string): Promise<void> {
  const allReports = await apiClient.get<ServerReport[]>('/api/reports');
  const drafts = allReports.filter(
    (r) => r.user_id === userId && r.month === month && r.is_draft === true,
  );
  await Promise.all(drafts.map((r) => apiClient.del(`/api/reports/${r.id}`)));

  // Also drop any locally-persisted offline drafts for this month so the
  // workflow doesn't restore a draft the user just discarded. Fail-soft: if
  // IndexedDB is unavailable this resolves to an empty list and no-ops.
  const localDrafts = await getAllLocalDrafts();
  await Promise.all(
    localDrafts.filter((d) => d.month === month).map((d) => deleteLocalDraft(d.localId)),
  );
}
